'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

interface ChartContextType {
  Highcharts: any;
  isHighchartsLoaded: boolean;
  isLoading: boolean;
  error: string | null;
}

const ChartContext = createContext<ChartContextType>({
  Highcharts: null,
  isHighchartsLoaded: false,
  isLoading: true,
  error: null,
});

// 모듈들은 자동으로 Highcharts에 등록됨 (side effect import)

export function ChartProvider({ children }: { children: ReactNode }) {
  const [isHighchartsLoaded, setIsHighchartsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [Highcharts, setHighcharts] = useState<any>(null);

  useEffect(() => {
    // 클라이언트 사이드에서만 Highcharts 설정
    if (typeof window !== 'undefined') {
      const loadHighcharts = async () => {
        try {
          setIsLoading(true);
          setError(null);

          // 동적으로 Highcharts와 모듈들을 로드
          const HighchartsModule = await import('highcharts');
          const Highcharts = HighchartsModule.default;

          // 모듈들을 순차적으로 로드
          await import('highcharts/modules/accessibility');
          await import('highcharts/modules/exporting');
          await import('highcharts/modules/heatmap');
          await import('highcharts/modules/treemap');

          setHighcharts(Highcharts);
          setIsHighchartsLoaded(true);
          console.log('Highcharts modules initialized successfully');
        } catch (err) {
          console.error('Chart libraries initialization failed:', err);
          setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
          setIsLoading(false);
        }
      };

      loadHighcharts();
    }
  }, []);

  return (
    <ChartContext.Provider
      value={{
        Highcharts,
        isHighchartsLoaded,
        isLoading,
        error,
      }}
    >
      {children}
    </ChartContext.Provider>
  );
}

export function useChart() {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a ChartProvider');
  }
  return context;
}
